import { Column } from 'react-table'
import * as Yup from 'yup'

import { Flex } from 'components'
import Badge, { BadgeColor } from 'components/Badge'
import { MenuDropdownProps } from 'components/MenuOptions'

import { ApiKey } from 'services/types/api-key'
import { Pagination } from 'types/global'
import { formatDatetime } from 'utils/datetime'

import { CellText, ColumnHeader, KeyCell } from './styles'
import { getValidationMessages } from 'utils/validation'

const { REQUIRED_LEAD_SOURCE, REQUIRED_SALES_PIPELINE } = getValidationMessages()

export const DEFAULT_PAGE = 1
export const DEFAULT_PAGE_SIZE = 100
export const DEFAULT_PAGINATION: Pagination<ApiKey> = {
  pageNumber: DEFAULT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
  sortBy: undefined,
}

export const StatusConfig: {
  [key in 'active' | 'deactivated']: { badge: { color: BadgeColor; text: string }; menuOptions: MenuDropdownProps[] }
} = {
  active: {
    badge: {
      color: 'green',
      text: 'Active',
    },
    menuOptions: [
      {
        icon: ['fal', 'pencil'],
        label: 'edit_key_lbl',
        value: 'edit',
      },
      {
        icon: ['fal', 'minus-octagon'],
        iconColor: 'red',
        label: 'deactivate_key_lbl',
        value: 'deactivate',
      },
    ],
  },
  deactivated: {
    badge: {
      color: 'gray',
      text: 'deactivated',
    },
    menuOptions: [
      {
        icon: ['far', 'repeat'],
        label: 'reactivate_key_lbl',
        value: 'reactivate',
      },
    ],
  },
}

export const columns: Column<ApiKey>[] = [
  {
    accessor: 'key',
    Cell: ({ row }) => <KeyCell keyValue={row.original.key} />,
    disableSortBy: true,
    Header: () => <ColumnHeader>API Keys</ColumnHeader>,
    id: 'key',
  },
  {
    accessor: 'rules',
    Cell: ({ row }) => (
      <Flex flexWrap="wrap" gap="0.5rem">
        {row.original.rules.map((rule, index) => {
          const ownerName = rule.lead_owner.team_name ?? `${rule.lead_owner.fname} ${rule.lead_owner.lname}`
          return <Badge key={index} color="lightGray" text={`${rule.campaign.campaign_name} - ${ownerName}`} />
        })}
      </Flex>
    ),
    disableSortBy: true,
    Header: () => <ColumnHeader>Rules</ColumnHeader>,
    id: 'rules',
    width: 300,
  },
  {
    accessor: 'remarks',
    Cell: ({ row }) => <CellText textTransform="none">{row.original.remarks ?? '-'}</CellText>,
    disableSortBy: true,
    Header: () => <ColumnHeader>Remarks</ColumnHeader>,
    id: 'remarks',
    width: 150,
  },
  {
    accessor: 'expiration_ts',
    Cell: ({ row }) => <CellText>{formatDatetime(row.original.expiration_ts, 'dd MMM, yyyy') ?? '-'}</CellText>,
    Header: () => <ColumnHeader>Expiry date</ColumnHeader>,
    id: 'expirationTs',
    width: 150,
  },
  {
    accessor: 'is_active',
    Cell: ({ row }) => <Badge {...StatusConfig[row.original.is_active ? 'active' : 'deactivated'].badge} />,
    Header: () => <ColumnHeader>status_lbl</ColumnHeader>,
    id: 'isActive',
    width: 80,
  },
]

export const getApiKeyLastPortion = (key: string): string => {
  const parts = key.split('.')
  return parts[parts.length - 1]
}

export const validationSchema = Yup.object({
  expiry_ts: Yup.date().nullable(),
  remarks: Yup.string().nullable(),
  rules: Yup.array()
    .of(
      Yup.object({
        campaign_id: Yup.string().nullable(),
        lead_source_id: Yup.string().required(REQUIRED_LEAD_SOURCE).typeError(REQUIRED_LEAD_SOURCE),
        lead_process_id: Yup.string().when('campaign_id', (campaign_id: string | null, schema: Yup.StringSchema) => {
          if (campaign_id) {
            return schema.nullable()
          }

          return schema.required(REQUIRED_SALES_PIPELINE).typeError(REQUIRED_SALES_PIPELINE)
        }),
        user_id: Yup.string().nullable(),
        team_id: Yup.string().nullable(),
      }),
    )
    .required(),
})
